import {
  CampaignsDatatableColumnId,
  CampaignsEvaluationsDatatableColumnId,
  CampaignsGroupBy,
  CampaignsGroupedItemPerimeterType,
  CampaignsGroupedItemStatus,
  CampaignsSortField,
  EvaluationStatus,
  EvaluationStatusTab,
  EvaluationsGroupBy,
  EvaluationsGroupedItemStatus,
  EvaluationsSortField,
} from '@/contexts/campaigns/Campaigns.interfaces'
import { NdsTagColor } from '@/components/NewDesignSystem/NdsTag/NdsTag.interfaces'
import { NdsIconType } from '@/components/NewDesignSystem/NdsIcon/NdsIcon.interfaces'
import { NdsColor } from '@/interfaces/NdsColors.interfaces'
import {
  DatatableSortingOrderBy,
  type DatatableSorting,
} from '@/components/NewDesignSystem/NdsDatatable/NdsDatatable.interfaces'

export const CAMPAIGNS_ROOT_ROUTE = 'campaigns_root'
export const CAMPAIGNS_ROUTE = 'campaign'
export const CAMPAIGNS_EVALUATIONS_ROUTE = 'evaluations'
export const CAMPAIGN_RESULTS_ROUTE = 'gcampaign_details'

export const CAMPAIGN_EVAL_QUESTIONNAIRE_VIEW = 'questionnaire'

export const EVALUATION_REVIEW_STATUS_TRANSLATION_KEY: Readonly<Record<EvaluationStatus, string>> = {
  [EvaluationStatus.APPROVED]: 'campaigns.evaluations.review.status.approved',
  [EvaluationStatus.PENDING_APPROVAL]: 'campaigns.evaluations.review.status.to_review',
  [EvaluationStatus.CHALLENGED]: 'campaigns.evaluations.review.status.rejected',
}

export const EVALUATION_REVIEW_STATUS_TAG_COLOR: Readonly<Record<EvaluationStatus, NdsTagColor>> = {
  [EvaluationStatus.APPROVED]: NdsTagColor.SUCCESS,
  [EvaluationStatus.PENDING_APPROVAL]: NdsTagColor.PRIMARY_100,
  [EvaluationStatus.CHALLENGED]: NdsTagColor.DANGER,
}

export const EVALUATION_REVIEW_STATUS_ICON: Readonly<Record<EvaluationStatus, NdsIconType>> = {
  [EvaluationStatus.APPROVED]: NdsIconType.CHECK,
  [EvaluationStatus.PENDING_APPROVAL]: NdsIconType.CIRCLE_DOT_DASHED,
  [EvaluationStatus.CHALLENGED]: NdsIconType.CLOSE,
}

export const EVALUATION_REVIEW_ICON_COLOR: Readonly<Record<EvaluationStatus, NdsColor>> = {
  [EvaluationStatus.APPROVED]: NdsColor.SUCCESS_500,
  [EvaluationStatus.PENDING_APPROVAL]: NdsColor.NEUTRAL_700,
  [EvaluationStatus.CHALLENGED]: NdsColor.DANGER_500,
}

export const EVALUATION_STATUS_TABS = {
  ...EvaluationStatusTab,
}

export const EVALUATION_STATUS_TABS_TRANSLATION_KEY: Readonly<Record<EvaluationStatusTab, string>> = {
  [EVALUATION_STATUS_TABS.IN_PROGRESS]: 'campaigns.evaluations.status_tab.in_progress',
  [EVALUATION_STATUS_TABS.SUBMITTED]: 'campaigns.evaluations.status_tab.submitted',
  [EVALUATION_STATUS_TABS.VALIDATED]: 'campaigns.evaluations.status_tab.validated',
}

export const EVALUATIONS_GROUPED_ITEM_STATUS_TRANSLATION_KEY: Readonly<Record<EvaluationsGroupedItemStatus, string>> = {
  [EvaluationsGroupedItemStatus.IN_PREPARATION]: 'campaigns.evaluations.datatable.statuses.in_preparation',
  [EvaluationsGroupedItemStatus.IN_PROGRESS]: 'campaigns.evaluations.datatable.statuses.in_progress',
  [EvaluationsGroupedItemStatus.SUBMITTED]: 'campaigns.evaluations.datatable.statuses.submitted',
  [EvaluationsGroupedItemStatus.VALIDATED]: 'campaigns.evaluations.datatable.statuses.validated',
}

export const EVALUATIONS_GROUPED_ITEM_STATUS_TAG_COLOR: Readonly<Record<EvaluationsGroupedItemStatus, NdsTagColor>> = {
  [EvaluationsGroupedItemStatus.IN_PREPARATION]: NdsTagColor.NEUTRAL,
  [EvaluationsGroupedItemStatus.IN_PROGRESS]: NdsTagColor.PRIMARY,
  [EvaluationsGroupedItemStatus.SUBMITTED]: NdsTagColor.SECONDARY,
  [EvaluationsGroupedItemStatus.VALIDATED]: NdsTagColor.SUCCESS,
}

export const EVALUATIONS_GROUP_BY_TRANSLATION_KEY: Readonly<Record<EvaluationsGroupBy, string>> = {
  [EvaluationsGroupBy.CAMPAIGN]: 'campaigns.evaluations.group_by.campaign',
  [EvaluationsGroupBy.PERIMETER]: 'campaigns.evaluations.group_by.perimeter',
  [EvaluationsGroupBy.POLICY]: 'campaigns.evaluations.group_by.policy',
  [EvaluationsGroupBy.QUESTIONNAIRE]: 'campaigns.evaluations.group_by.questionnaire',
  [EvaluationsGroupBy.CONTRIBUTOR]: 'campaigns.evaluations.group_by.contributor',
  [EvaluationsGroupBy.MANAGER]: 'campaigns.evaluations.group_by.manager',
  [EvaluationsGroupBy.STATUS]: 'campaigns.evaluations.group_by.status',
}

export const EVALUATIONS_HIDDEN_COLUMN_ID_BY_GROUP_BY: Readonly<
  Partial<Record<EvaluationsGroupBy, CampaignsEvaluationsDatatableColumnId>>
> = {
  [EvaluationsGroupBy.CAMPAIGN]: CampaignsEvaluationsDatatableColumnId.CAMPAIGN,
  [EvaluationsGroupBy.PERIMETER]: CampaignsEvaluationsDatatableColumnId.PERIMETER,
  [EvaluationsGroupBy.POLICY]: CampaignsEvaluationsDatatableColumnId.POLICY,
  [EvaluationsGroupBy.QUESTIONNAIRE]: CampaignsEvaluationsDatatableColumnId.QUESTIONNAIRE,
  [EvaluationsGroupBy.CONTRIBUTOR]: CampaignsEvaluationsDatatableColumnId.CONTRIBUTORS,
  [EvaluationsGroupBy.MANAGER]: CampaignsEvaluationsDatatableColumnId.MANAGERS,
  [EvaluationsGroupBy.STATUS]: CampaignsEvaluationsDatatableColumnId.STATUS,
}

export const CAMPAIGNS_GROUPED_ITEM_STATUS_TRANSLATION_KEY: Readonly<Record<CampaignsGroupedItemStatus, string>> = {
  [CampaignsGroupedItemStatus.IN_PREPARATION]: 'campaigns.datatable.statuses.in_preparation',
  [CampaignsGroupedItemStatus.LAUNCHED]: 'campaigns.datatable.statuses.launched',
  [CampaignsGroupedItemStatus.IN_PROGRESS]: 'campaigns.datatable.statuses.in_progress',
  [CampaignsGroupedItemStatus.FINISHED]: 'campaigns.datatable.statuses.finished',
}

export const CAMPAIGNS_GROUPED_ITEM_STATUS_TAG_COLOR: Readonly<Record<CampaignsGroupedItemStatus, NdsTagColor>> = {
  [CampaignsGroupedItemStatus.IN_PREPARATION]: NdsTagColor.NEUTRAL,
  [CampaignsGroupedItemStatus.LAUNCHED]: NdsTagColor.SECONDARY,
  [CampaignsGroupedItemStatus.IN_PROGRESS]: NdsTagColor.PRIMARY,
  [CampaignsGroupedItemStatus.FINISHED]: NdsTagColor.SUCCESS,
}

export const CAMPAIGNS_GROUPED_ITEM_PERIMETER_TYPE_ICON: Readonly<
  Partial<Record<CampaignsGroupedItemPerimeterType, NdsIconType>>
> = {
  [CampaignsGroupedItemPerimeterType.INTERNAL]: NdsIconType.BOX,
  [CampaignsGroupedItemPerimeterType.TAG]: NdsIconType.BOXES,
  [CampaignsGroupedItemPerimeterType.TREE]: NdsIconType.NETWORK,
}

export const CAMPAIGNS_GROUP_BY_TRANSLATION_KEY: Readonly<Record<CampaignsGroupBy, string>> = {
  [CampaignsGroupBy.PERIMETER]: 'campaigns.group_by.perimeter',
  [CampaignsGroupBy.POLICY]: 'campaigns.group_by.policy',
  [CampaignsGroupBy.QUESTIONNAIRE]: 'campaigns.group_by.questionnaire',
  [CampaignsGroupBy.MANAGER]: 'campaigns.group_by.manager',
  [CampaignsGroupBy.STATUS]: 'campaigns.group_by.status',
}

export const CAMPAIGNS_HIDDEN_COLUMN_ID_BY_GROUP_BY: Readonly<Record<CampaignsGroupBy, CampaignsDatatableColumnId>> = {
  [CampaignsGroupBy.PERIMETER]: CampaignsDatatableColumnId.PERIMETER,
  [CampaignsGroupBy.POLICY]: CampaignsDatatableColumnId.POLICY,
  [CampaignsGroupBy.QUESTIONNAIRE]: CampaignsDatatableColumnId.QUESTIONNAIRE,
  [CampaignsGroupBy.MANAGER]: CampaignsDatatableColumnId.MANAGERS,
  [CampaignsGroupBy.STATUS]: CampaignsDatatableColumnId.STATUS,
}

export const EVALUATIONS_SORT_FIELD_BY_COLUMN_ID: Readonly<
  Partial<Record<CampaignsEvaluationsDatatableColumnId, EvaluationsSortField>>
> = {
  [CampaignsEvaluationsDatatableColumnId.CAMPAIGN]: EvaluationsSortField.CAMPAIGN,
  [CampaignsEvaluationsDatatableColumnId.PERIMETER]: EvaluationsSortField.PERIMETER,
  [CampaignsEvaluationsDatatableColumnId.STATUS]: EvaluationsSortField.STATUS,
  [CampaignsEvaluationsDatatableColumnId.END_DATE]: EvaluationsSortField.VALIDATION_DATE,
}

export const DEFAULT_EVALUATIONS_SORTING: Readonly<DatatableSorting> = {
  columnId: CampaignsEvaluationsDatatableColumnId.CAMPAIGN,
  orderBy: DatatableSortingOrderBy.ASC,
}

export const CAMPAIGNS_SORT_FIELD_BY_COLUMN_ID: Readonly<
  Partial<Record<CampaignsDatatableColumnId, CampaignsSortField>>
> = {
  [CampaignsDatatableColumnId.NAME]: CampaignsSortField.NAME,
  [CampaignsDatatableColumnId.QUESTIONNAIRE]: CampaignsSortField.QUESTIONNAIRE,
  [CampaignsDatatableColumnId.POLICY]: CampaignsSortField.POLICY,
  [CampaignsDatatableColumnId.STATUS]: CampaignsSortField.STATUS,
  [CampaignsDatatableColumnId.END_DATE]: CampaignsSortField.END_DATE,
}

export const DEFAULT_CAMPAIGNS_SORTING: Readonly<DatatableSorting> = {
  columnId: CampaignsDatatableColumnId.NAME,
  orderBy: DatatableSortingOrderBy.ASC,
}

export const EVALUATION_EDIT_DEFAULT_CURRENT_OBJ = {
  eval_level_id: null,
  percentage: null,
  comments: '',
  remediations: [],
  measures: [],
}
